import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

interface PromptFocusedFilesProps {
  hasAttachments: boolean;
}

const TRANSITION = { duration: 0.2, ease: cubicEasingFn };

export const PromptFocusedFiles = memo<PromptFocusedFilesProps>(({ hasAttachments }) => {
  const contentDiv = useRef<HTMLDivElement>(null);

  const lockedFiles = useStore(workbenchStore.lockedFiles);
  const targetedFiles = useStore(workbenchStore.targetedFiles);
  const [contentHeight, setContentHeight] = useState(0);

  const showFocusedFiles = useCallback(() => workbenchStore.currentFileTreeView.set('focused'), []);

  useEffect(() => {
    if (contentDiv.current) {
      setContentHeight(contentDiv.current.getBoundingClientRect().height);
    }
  }, []);

  const lockedFilesCount = Object.keys(lockedFiles).reduce(
    (count, file) => (lockedFiles[file]?.type === 'file' ? count + 1 : count),
    0,
  );

  const targetedFilesCount = Object.keys(targetedFiles).reduce(
    (count, file) => (targetedFiles[file]?.type === 'file' ? count + 1 : count),
    0,
  );

  const visible = lockedFilesCount > 0 || targetedFilesCount > 0;

  const lockedFilesText = `${lockedFilesCount} file${lockedFilesCount > 1 ? 's' : ''} ${lockedFilesCount > 1 ? 'are' : 'is'} locked from AI changes`;

  const inspectedElement = useStore(workbenchStore.inspectedElement);

  return (
    <motion.div
      transition={TRANSITION}
      animate={{ height: visible ? contentHeight : 0 }}
      initial={false}
      className={classNames('border-b-px', {
        'bg-bolt-elements-background-depth-1': hasAttachments,
        'border-bolt-elements-borderColor': hasAttachments,
        'border-transparent': !hasAttachments || !visible,
        relative: hasAttachments && visible,
      })}
    >
      <div
        className={classNames(
          'overflow-hidden h-full border-bolt-elements-borderColor relative bg-bolt-elements-background-depth-2 transition-opacity duration-200',
          {
            // this rounded-t-[0.44rem] should really just be a rounded-t-lg but there's a rendering issue if we use that value
            'rounded-t-[0.44rem]': !Boolean(inspectedElement),
            'border-px -left-px w-[calc(100%+2px)] h-[calc(100%+1px)]': !inspectedElement && hasAttachments,
            'border-b-px left-0 right-0': !hasAttachments,
            'opacity-0': !visible,
            'opacity-100': visible,
          },
        )}
      >
        <div ref={contentDiv} className="flex text-xs py-1.5 px-2.5 font-medium">
          {targetedFilesCount > 0 && (
            <div>
              AI changes limited to {targetedFilesCount} file{targetedFilesCount > 1 ? 's' : ''}
            </div>
          )}
          {targetedFilesCount === 0 && lockedFilesCount >= 1 && <div>{lockedFilesText}</div>}
          <div className="flex-grow" />
          <button className="bg-transparent text-bolt-elements-link hover:underline" onClick={showFocusedFiles}>
            Update
          </button>
        </div>
      </div>
    </motion.div>
  );
});
